import type { FluxErrorCode } from '../errors.codes.js';
import type { FluxErrorCompleteMetadata } from '../utils/get-flux-error-metadata.util.js';
import { getFluxErrorMetadata } from '../utils/get-flux-error-metadata.util.js';

/**
 * Classe que representa um erro específico do Flux. Essa classe é responsável por encapsular
 * informações detalhadas sobre o erro, incluindo seu código, mensagem e metadata associada.
 */
export class FluxError extends Error {
  public static isFluxError(error: unknown): error is FluxError {
    return error instanceof FluxError;
  }

  public readonly metadata: FluxErrorCompleteMetadata;

  /**
   * Construtor da classe FluxError. Caso o código do erro fornecido não exista no objeto de códigos de erro,
   * um erro com o código `UNKNOWN` será lançado.
   *
   * @param code - Código do erro a ser lançado
   * @param message - Mensagem opcional para o erro, por padrão será usada a mensagem do metadata. **Não é recomendado
   * fornecer uma mensagem personalizada**, a menos que seja estritamente necessário, para garantir consistência nas mensagens de erro.
   */
  public constructor(
    public code: FluxErrorCode,
    message?: string | null,
  ) {
    const metadata = getFluxErrorMetadata(code);

    super(message ?? metadata.message);
    this.name = 'FluxError';
    this.metadata = metadata;
  }

  public override toString(): string {
    return `${this.name}(${this.code}): ${this.message}`;
  }

  public toJSON(): {
    code: FluxErrorCode;
    message: string;
    metadata: ReturnType<typeof getFluxErrorMetadata>;
    name: string;
  } {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      metadata: this.metadata,
    };
  }
}

/**
 * Função utilitária para lançar um erro do tipo FluxError de forma simplificada.
 *
 * @param code - Código do erro a ser lançado
 * @param message - Mensagem opcional para o erro, por padrão será usada a mensagem do metadata
 * @throws {@link FluxError} - Lança um erro do tipo FluxError com o código e mensagem fornecidos
 */
export function throwFluxError(code: FluxErrorCode, message?: string | null): never {
  throw new FluxError(code, message);
}
